/**
 * Base class for a Bootstrap Modal window.
 * http://getbootstrap.com/javascript/#modals
 *
 * Expects to be passed (el) an element ID and an optional model
 * (rvc/models/datatable-link).
 *
 * The markup associated with the element ID must already be present and will be
 * used to attach the modal window to the DOM.
 */

import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import FooterActions from "../footer-actions";

const channel = Radio.channel("global");

const DEFAULTS = {
  template: false,
  title: "&nbsp;",
  modalOptions: {
    // removes the ability to dismiss model via escape key
    keyboard: false,
    // removes the ability to dismiss model by clicking on modal backdrop
    backdrop: "static",
  },
  destroyOnDismiss: true,
};

const REGIONS = {
  title: ".modal-title",
  body: ".modal-body",
  footer: ".modal-footer > .actions",
};

class BaseModal extends Marionette.LayoutView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  childEvents() {
    return {
      "dismiss:modal": "_hide",

      // can be used by subclasses that disable destroyOnDismiss to destroy the
      // modal instance after it is no longer needed
      "destroy:modal": "_destroy",
    };
  }

  initialize() {
    this.destroyOnDismiss = !!this.options.destroyOnDismiss;
    this.className = this.options.className || null;
    this.returnRoute = this.options.returnRoute || null;
    this.returnRouteRefresh = this.options.returnRouteRefresh || false;
    this.modalAPI = null;

    this.render();
  }

  onBeforeRender() {
    this._applyCustomClass();
    this._addRegions();
    this._setTitle();
    this._setActionButtons();
  }

  onRender() {
    this.modalAPI = this.$el

      // handle model events
      // http://getbootstrap.com/javascript/#modals-events
      .on("show.bs.modal", this._onShow.bind(this))
      .on("shown.bs.modal", this._onShown.bind(this))
      .on("hide.bs.modal", this._onHide.bind(this))
      .on("hidden.bs.modal", this._onHidden.bind(this))

      // instantiate bootstrap modal
      .modal(this.options.modalOptions);
  }

  /**
   * Determines the route, if any, to load on modal dismissal.
   *
   * This allows a subclass to configure an instance property named
   * returnRoute set to a valid route or "back." When a returnRoute is not
   * configured, the default route will be invoked.
   */
  onBeforeDestroy() {
    // make sure the modal has been hidden
    this._hide();

    // handle return route
    if (this.returnRoute) {
      if ("back" === this.returnRoute) {
        // request previous route
        channel.request("router:previousRoute", {
          reload: !!this.returnRouteRefresh,
        });
      } else {
        // request configured route
        channel.request("router:returnToRoute", this.returnRoute, {
          reload: !!this.returnRouteRefresh,
        });
      }
    } else {
      // redirect home
      channel.request("router:defaultRoute", {
        reload: !!this.returnRouteRefresh,
      });
    }
  }

  _applyCustomClass() {
    this.className && this.$el.addClass(this.className);
  }

  _addRegions() {
    this.addRegions(REGIONS);
  }

  _setTitle() {
    this.title.$el.html(this.options.title || null);
  }

  _setActionButtons() {
    this.footer.show(new FooterActions());
  }

  _onShow() {
    // no-op
  }

  _onShown() {
    // no-op
  }

  _onHide() {
    // no-op
  }

  _onHidden() {
    // destroy layout view after modal is dismissed
    this.destroyOnDismiss && this.destroy();
  }

  // dismisses modal by calling its API
  _hide() {
    try {
      this.modalAPI.modal("hide");
    } catch (e) {
      console.warn("Unable to dismiss modal", this);
    }
  }

  _destroy() {
    this.destroy();
  }
}

export { BaseModal as default };
